import { z } from 'zod';
import { cargoGroupSchema, routeSegmentPointKindSchema, transportModeSchema, transportUnitSchema, } from './orders';
/**
 * Zod-схемы для перевозок, событий груза, геотрека и перевозчиков.
 * Закрывает ПП РФ № 1317 п.3 ж), з), к).
 */
// =====================================================================
// Enums
// =====================================================================
export const shipmentStatusSchema = z.enum([
    'PROJECT',
    'CREATED',
    'PICKED_UP',
    'IN_TRANSIT',
    'ARRIVED',
    'DELIVERED',
    'CLOSED',
    'CANCELLED',
]);
export const cargoEventTypeSchema = z.enum([
    'RECEIVED',
    'HANDLING',
    'LOADED',
    'STORED',
    'IN_TRANSIT',
    'UNLOADED',
    'HANDED_OVER',
    'DELIVERED',
]);
// =====================================================================
// Carrier — справочник перевозчиков экспедитора
// =====================================================================
import { clientTypeSchema } from './client-profile';
/** Роль привлекаемого исполнителя. */
export const engagedPartyRoleSchema = z.enum([
    'CARRIER',
    'CUSTOMS_BROKER',
    'WAREHOUSE',
    'FORWARDER',
    'OTHER',
]);
/** Подписи ролей привлекаемых исполнителей (для UI). */
export const ENGAGED_PARTY_ROLE_LABEL = {
    CARRIER: 'Перевозчик',
    CUSTOMS_BROKER: 'Таможенный брокер',
    WAREHOUSE: 'Склад / СВХ',
    FORWARDER: 'Экспедитор / агент',
    OTHER: 'Иное',
};
export const carrierCreateSchema = z.object({
    type: clientTypeSchema,
    role: engagedPartyRoleSchema.default('CARRIER'),
    fullName: z.string().min(1).max(500),
    shortName: z.string().max(500).optional(),
    inn: z.string().max(20).optional(),
    /** Юридический (почтовый) адрес — идёт в стороны ЭТрН / поручения. */
    address: z.string().max(500).optional(),
    contactPhone: z.string().max(50).optional(),
    contactEmail: z.string().email().optional(),
    /** Код абонента в системе ЭДО (МИГ24) — адрес для электронных документов. */
    edoSubscriberId: z.string().max(100).optional(),
    /** Ставка НДС лица (как priceVatRate): '-1'/'0'/'5'/'7'/'10'/'20'/'22'. */
    vatRate: z.string().max(10).optional(),
    notes: z.string().max(2000).optional(),
});
export const carrierPatchSchema = carrierCreateSchema.partial();
export const carrierDtoSchema = z.object({
    id: z.string(),
    type: clientTypeSchema,
    role: engagedPartyRoleSchema,
    fullName: z.string(),
    shortName: z.string().nullable(),
    inn: z.string().nullable(),
    address: z.string().nullable(),
    contactPhone: z.string().nullable(),
    contactEmail: z.string().nullable(),
    edoSubscriberId: z.string().nullable(),
    vatRate: z.string().nullable(),
    notes: z.string().nullable(),
    createdAt: z.string().datetime(),
    archivedAt: z.string().datetime().nullable(),
});
// =====================================================================
// ContractParty — привязка перевозчика к контракту
// =====================================================================
/** Роль привлечённого лица в договоре/перевозке (значение → русская подпись). */
export const CONTRACT_PARTY_ROLE_LABEL = {
    PRIMARY_CARRIER: 'Основной перевозчик',
    SUBCONTRACTOR: 'Субподрядчик',
    FORWARDER: 'Экспедитор-агент',
    CUSTOMS_BROKER: 'Таможенный брокер',
};
/** Подпись роли в договоре; неизвестное значение возвращается как есть. */
export function contractPartyRoleLabel(role) {
    return CONTRACT_PARTY_ROLE_LABEL[role] ?? role;
}
/** Опции роли для формы привязки. */
export const CONTRACT_PARTY_ROLE_OPTIONS = Object.entries(CONTRACT_PARTY_ROLE_LABEL).map(([value, label]) => ({ value, label }));
export const contractPartyCreateSchema = z.object({
    carrierId: z.string().optional(),
    /** PRIMARY_CARRIER, SUBCONTRACTOR, FORWARDER, CUSTOMS_BROKER. */
    role: z.string().min(1).max(50),
    notes: z.string().max(2000).optional(),
});
export const contractPartyDtoSchema = z.object({
    id: z.string(),
    contractId: z.string(),
    carrierId: z.string().nullable(),
    role: z.string(),
    notes: z.string().nullable(),
    carrier: carrierDtoSchema.nullable().optional(),
});
// =====================================================================
// CargoEvent
// =====================================================================
export const cargoEventCreateSchema = z.object({
    type: cargoEventTypeSchema,
    /** ISO datetime — момент события (фронт может проставить now или дать ввести). */
    occurredAt: z.string().datetime(),
    locationName: z.string().max(500).optional(),
    latitude: z.number().gte(-90).lte(90).optional(),
    longitude: z.number().gte(-180).lte(180).optional(),
    responsibleUserId: z.string().optional(),
    comment: z.string().max(5000).optional(),
});
export const cargoEventDtoSchema = z.object({
    id: z.string(),
    shipmentId: z.string(),
    type: cargoEventTypeSchema,
    occurredAt: z.string().datetime(),
    locationName: z.string().nullable(),
    latitude: z.number().nullable(),
    longitude: z.number().nullable(),
    responsibleUserId: z.string().nullable(),
    comment: z.string().nullable(),
});
// =====================================================================
// GeoTrackPoint
// =====================================================================
export const geoTrackPointCreateSchema = z.object({
    occurredAt: z.string().datetime(),
    latitude: z.number().gte(-90).lte(90),
    longitude: z.number().gte(-180).lte(180),
    speed: z.number().nonnegative().optional(),
    source: z.string().max(100).optional(),
});
/** Batch — массив точек. Для импорта из телематики. */
export const geoTrackBatchSchema = z.object({
    points: z.array(geoTrackPointCreateSchema).min(1).max(10000),
});
export const geoTrackPointDtoSchema = z.object({
    id: z.string(),
    shipmentId: z.string(),
    occurredAt: z.string().datetime(),
    latitude: z.number(),
    longitude: z.number(),
    speed: z.number().nullable(),
    source: z.string().nullable(),
});
// =====================================================================
// ShipmentLeg — плечо перевозки (участок маршрута + исполнитель)
// =====================================================================
/**
 * Плечо перевозки: участок маршрута (от/до, вид транспорта, даты) + исполнитель
 * (привлекаемый исполнитель) + себестоимость. Объединяет «плечо» и «привлекаемый исполнитель».
 */
export const shipmentLegInputSchema = z.object({
    sequence: z.number().int().min(1),
    transportMode: transportModeSchema,
    fromKind: routeSegmentPointKindSchema,
    fromName: z.string().max(500),
    fromCountry: z.string().max(100).optional(),
    fromCountryCode: z.string().max(10).optional(),
    toKind: routeSegmentPointKindSchema,
    toName: z.string().max(500),
    toCountry: z.string().max(100).optional(),
    toCountryCode: z.string().max(10).optional(),
    dateFrom: z.string().optional(),
    dateTo: z.string().optional(),
    /** Исполнитель плеча — привлекаемый исполнитель (id из справочника). */
    carrierId: z.string().nullable().optional(),
    /** Себестоимость плеча — что платим исполнителю (для сметы). */
    price: z.number().nonnegative().nullable().optional(),
    priceVatRate: z.string().max(10).nullable().optional(),
    priceCurrency: z.string().max(10).optional(),
    comment: z.string().max(2000).optional(),
});
export const shipmentLegDtoSchema = shipmentLegInputSchema.extend({
    id: z.string(),
    shipmentId: z.string(),
    dateFrom: z.string().nullable().optional(),
    dateTo: z.string().nullable().optional(),
    price: z.number().nullable().optional(),
    /** Наименование исполнителя (для отображения в списке). */
    carrierName: z.string().nullable().optional(),
    /** Номера выпущенных на плечо документов (ЭТрН / поручение ФНС). */
    documentNumbers: z.array(z.string()).default([]),
});
/**
 * Внутренняя смета перевозки: агрегат себестоимости плеч (что платим
 * привлекаемым лицам), сгруппированный по валюте, с выделением НДС.
 * Цена плеча трактуется как сумма С НДС по ставке плеча.
 */
export function computeShipmentEstimate(legs) {
    const byCurrency = new Map();
    let pricedLegs = 0;
    for (const l of legs) {
        if (l.price == null)
            continue;
        pricedLegs += 1;
        const currency = l.priceCurrency ?? 'RUB';
        const rate = Number(l.priceVatRate);
        const gross = l.price;
        const vat = Number.isFinite(rate) && rate > 0 ? (gross * rate) / (100 + rate) : 0;
        const line = byCurrency.get(currency) ?? { currency, gross: 0, vat: 0, net: 0, legCount: 0 };
        line.gross += gross;
        line.vat += vat;
        line.net += gross - vat;
        line.legCount += 1;
        byCurrency.set(currency, line);
    }
    const round2 = (n) => Math.round(n * 100) / 100;
    const lines = [...byCurrency.values()].map((l) => ({
        ...l,
        gross: round2(l.gross),
        vat: round2(l.vat),
        net: round2(l.net),
    }));
    return {
        lines,
        totalLegs: legs.length,
        pricedLegs,
        unpricedLegs: legs.length - pricedLegs,
    };
}
// =====================================================================
// Shipment
// =====================================================================
export const shipmentListItemSchema = z.object({
    id: z.string(),
    status: shipmentStatusSchema,
    startedAt: z.string().datetime().nullable(),
    finishedAt: z.string().datetime().nullable(),
    contractId: z.string().nullable(),
    contractNumber: z.string().nullable(),
    orderId: z.string(),
    orderNumber: z.string(),
    cargoName: z.string(),
    clientId: z.string(),
    clientDisplayName: z.string().nullable().optional(),
});
export const shipmentDetailSchema = shipmentListItemSchema.extend({
    events: z.array(cargoEventDtoSchema),
    geoTrack: z.array(geoTrackPointDtoSchema),
    parties: z.array(contractPartyDtoSchema),
    /** Плечи перевозки (участок + исполнитель) — фаза 2. */
    legs: z.array(shipmentLegDtoSchema).default([]),
    shipmentType: z.string().nullable().optional(),
    sameCargoForAll: z.boolean().optional(),
    cargoGroups: z.array(cargoGroupSchema).nullable().optional(),
    transportUnits: z.array(transportUnitSchema).optional(),
    /**
     * Экспедитор принимает груз во владение (из заказа). Определяет, от чьего
     * имени формируются документы плеч (поручение/ЭТрН/ЭЗЗ): да — от нашего
     * экспедитора, нет — от клиента.
     */
    expeditorTakesPossession: z.boolean().nullable().optional(),
});
/** Переключение отметки «экспедитор принимает груз во владение» на перевозке. */
export const shipmentPossessionSchema = z.object({
    expeditorTakesPossession: z.boolean(),
});
//# sourceMappingURL=shipments.js.map